/**
 * Vozidla helpers — vehicle-domain mapping logic.
 *
 * Battery thresholds mirror the legacy logic: a final state is the worst of
 * `days-since-charge` and `last-battery-level`. Status filter values map to
 * the canonical `stav_vozidla` strings the API accepts.
 */

import type { BatteryLevel } from '@/components/BatteryDot';
import { searchParamString } from '@/lib/list-search';
import type { Vehicle } from './api';

export type StavFilter =
  | 'all'
  | 'skladovy'
  | 'registrovany'
  | 'zakaznicky'
  | 'predany_dealerovi'
  | 'predany_zakaznikovi'
  | 'prevedeno';

/** Maps the React filter key to the literal `stav_vozidla` value the API accepts.
 *  Note: passing `Skladový` to the API returns BOTH `Skladový` and `Registrovaný`
 *  rows (see `api/src/repositories/vehicle.repository.ts:73-77`) — that's the
 *  legacy default "in-stock + registered for delivery" view. */
export const STAV_VALUE: Record<Exclude<StavFilter, 'all'>, string> = {
  skladovy: 'Skladový',
  registrovany: 'Registrovaný',
  zakaznicky: 'Zákaznický',
  predany_dealerovi: 'Předaný dealerovi',
  predany_zakaznikovi: 'Předaný zákazníkovi',
  prevedeno: 'Převedeno',
};

export const STAV_LABEL: Record<StavFilter, string> = {
  all: 'Všechna',
  skladovy: 'Skladová',
  registrovany: 'Registrovaná',
  zakaznicky: 'Zákaznická',
  predany_dealerovi: 'Předaná dealerovi',
  predany_zakaznikovi: 'Předaná zákazníkovi',
  prevedeno: 'Převedená do jiné firmy',
};

/** Row badge tone: a transferred car is retired (neutral), a registered one amber, the rest green. */
export function stavBadgeTone(v: { stav_vozidla: string }): 'neutral' | 'warn' | 'ok' {
  if (v.stav_vozidla === 'Převedeno') return 'neutral';
  return v.stav_vozidla === 'Registrovaný' ? 'warn' : 'ok';
}

/** True for vehicles the artboard tints amber. */
export function isRegistrovany(v: Vehicle): boolean {
  return v.stav_vozidla === 'Registrovaný';
}

/** Vehicles paid out by FMC — tinted yellow in the legacy list. */
export function isVyplaceny(v: Pick<Vehicle, 'vyplaceno_fmc'>): boolean {
  const raw = v.vyplaceno_fmc;
  return !!raw && raw !== '' && raw !== '0';
}

/** Compact stav label for the row badge (Registrovaný stays whole — the stav columns are sized for it). */
export function stavBadgeLabel(stav: string): string {
  if (stav === 'Skladový') return 'Skladový';
  if (stav === 'Zákaznický') return 'Zákazn.';
  if (stav === 'Předaný zákazníkovi') return 'Předáno';
  if (stav === 'Předaný dealerovi') return 'Dealer';
  if (stav === 'Převedeno') return 'Převedeno';
  return stav;
}

export function batteryLevel(v: Pick<Vehicle, 'last_battery_level' | 'days_since_charge'>): BatteryLevel {
  const pct = v.last_battery_level;
  const days = v.days_since_charge;
  if (pct == null || days == null) return 'unknown';
  const daysStatus: BatteryLevel = days <= 30 ? 'high' : days <= 50 ? 'medium' : 'low';
  const pctStatus: BatteryLevel = pct > 90 ? 'high' : pct >= 75 ? 'medium' : 'low';
  if (daysStatus === 'low' || pctStatus === 'low') return 'low';
  if (daysStatus === 'medium' || pctStatus === 'medium') return 'medium';
  return 'high';
}

/** Display name like `Ford Explorer` from the joined columns. */
export function vehicleDisplayName(v: Vehicle): string {
  const brand = v.znacka_nazev?.trim() || '';
  const model = v.nazev_modelu?.trim() || '';
  return [brand, model].filter(Boolean).join(' ');
}

/**
 * Search-param shape shared between /vozidla and /vozidla/$id.
 * Persisting these in the URL means the narrow rail on the detail page
 * matches the filter the user had on the list page (and bookmarks /
 * back-forward navigation work as expected).
 */
export interface VozidlaSearch {
  stav?: StavFilter;
  brand?: number;
  model?: number;
  search?: string;
  /** Delivery month filter as `YYYY-MM`. Mirrors the legacy "Měsíc předání"
   *  field, which maps to a datum_predani range. */
  deliveryMonth?: string;
}

/** `YYYY-MM`, month 01–12. */
const DELIVERY_MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

// Derived from the filter map, so a new state can't be added to the menu and forgotten here.
const VALID_STAV: ReadonlySet<StavFilter> = new Set<StavFilter>(['all', ...(Object.keys(STAV_VALUE) as StavFilter[])]);

/** TanStack Router `validateSearch` — coerces unknown query strings into
 *  the typed shape, dropping anything we don't recognize. */
export function parseVozidlaSearch(input: Record<string, unknown>): VozidlaSearch {
  const out: VozidlaSearch = {};
  if (typeof input.stav === 'string' && VALID_STAV.has(input.stav as StavFilter)) {
    out.stav = input.stav as StavFilter;
  }
  if (input.brand != null) {
    const n = Number(input.brand);
    if (Number.isFinite(n) && n > 0) out.brand = n;
  }
  if (input.model != null && out.brand != null) {
    const n = Number(input.model);
    if (Number.isFinite(n) && n > 0) out.model = n;
  }
  const search = searchParamString(input.search);
  if (search) out.search = search;
  if (typeof input.deliveryMonth === 'string' && DELIVERY_MONTH_RE.test(input.deliveryMonth)) {
    out.deliveryMonth = input.deliveryMonth;
  }
  return out;
}

/**
 * Default stav filter — kept implicit in the URL (no `?stav=skladovy` clutter
 * when it's the default), but every consumer must apply this same fallback so
 * the list and the detail-page narrow rail stay in sync.
 */
export const DEFAULT_STAV: StavFilter = 'skladovy';

/** Build the filter object the API queries take from the shared search shape. */
export function vozidlaFiltersFromSearch(s: VozidlaSearch): {
  brand?: string;
  model?: string;
  status?: string;
  search?: string;
  delivery_from?: string;
  delivery_to?: string;
} {
  const filters: {
    brand?: string;
    model?: string;
    status?: string;
    search?: string;
    delivery_from?: string;
    delivery_to?: string;
  } = {};
  if (s.brand != null) filters.brand = String(s.brand);
  if (s.model != null && s.brand != null) filters.model = String(s.model);
  // Treat missing stav as the default so a bare `/vozidla/$id` URL
  // matches what the user was browsing on `/vozidla`.
  const stav: StavFilter = s.stav ?? DEFAULT_STAV;
  if (stav !== 'all') filters.status = STAV_VALUE[stav];
  if (s.search) filters.search = s.search;
  const bounds = deliveryMonthBounds(s.deliveryMonth);
  if (bounds) {
    filters.delivery_from = bounds.from;
    filters.delivery_to = bounds.to;
  }
  return filters;
}

/** Convert a `YYYY-MM` delivery month into the inclusive datum_predani range
 *  the API filters on (first day → last day). Returns null for absent / malformed input. */
export function deliveryMonthBounds(
  month: string | undefined,
): { from: string; to: string } | null {
  if (!month || !DELIVERY_MONTH_RE.test(month)) return null;
  const [y, m] = month.split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  return { from: `${month}-01`, to: `${month}-${String(lastDay).padStart(2, '0')}` };
}

const csMonthFormatter = new Intl.DateTimeFormat('cs-CZ', { month: 'long' });

/** Human label for a `YYYY-MM` value, e.g. `Květen 2026`. */
export function deliveryMonthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  const name = csMonthFormatter.format(new Date(y, m - 1, 1));
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${y}`;
}

/** Generate the month options for the filter menu, newest first: a window
 *  from `ahead` months in the future down through `back` months in the past,
 *  grouped client-side by year. */
export function deliveryMonthOptions(
  now: Date = new Date(),
  { back = 36, ahead = 6 }: { back?: number; ahead?: number } = {},
): { value: string; label: string; year: number; month: number }[] {
  const out: { value: string; label: string; year: number; month: number }[] = [];
  for (let i = ahead; i >= -back; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const value = `${year}-${String(month).padStart(2, '0')}`;
    out.push({ value, label: deliveryMonthLabel(value), year, month });
  }
  return out;
}
